import { CircleHelp, EyeOff, Lock } from 'lucide-react'
import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { HIDDEN_MESSAGE } from './sheetMessages'

function Hint({ tip, children }: { tip: string; children: ReactNode }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent>{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// A button rather than Badge's span, so the tooltip also opens on keyboard focus.
function HintBadge({
  tip,
  variant,
  className,
  children,
}: {
  tip: string
  variant: 'warning' | 'outline' | 'secondary'
  className?: string
  children: ReactNode
}) {
  return (
    <Hint tip={tip}>
      <Badge asChild variant={variant} className={className}>
        <button type="button">{children}</button>
      </Badge>
    </Hint>
  )
}

function Help({
  label,
  tip,
  className,
}: {
  label: string
  tip: string
  className?: string
}) {
  return (
    <Hint tip={tip}>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label={label}
        className={cn(
          'size-5 text-neutral-low-contrast hover:text-primary-high-contrast',
          className
        )}>
        <CircleHelp aria-hidden="true" className="size-3.5" />
      </Button>
    </Hint>
  )
}

export function OutOfDateBadge({ orgName }: { orgName: string }) {
  return (
    <HintBadge
      variant="warning"
      tip={`Careforce changed this snippet after ${orgName} customized it.`}>
      Out of date
    </HintBadge>
  )
}

export function ManagedByBadge({ orgName }: { orgName: string }) {
  return (
    <HintBadge variant="outline" tip={`Only ${orgName} can edit or reset it.`}>
      <Lock aria-hidden="true" />
      {`Managed by ${orgName}`}
    </HintBadge>
  )
}

export function HiddenHintBadge() {
  return (
    <HintBadge variant="secondary" tip={HIDDEN_MESSAGE} className="shrink-0">
      <EyeOff aria-hidden="true" />
      Hidden
    </HintBadge>
  )
}

export function VariantsHelp() {
  return (
    <Help
      label="About variants"
      tip="Duties with their own wording of this snippet. Only a customized organization can have them."
    />
  )
}

export function SnippetEditsHelp() {
  return (
    <Help
      label="About snippet edits"
      tip="Snippets are not part of Review or Live. An edit reaches every call right away, so publishing does not change it."
    />
  )
}
